import { useMemo, useState } from "react";
import MetricGrid from "@/components/MetricGrid";
import type { MetricCardType, MetricCardVariant } from "@/components/MetricCard/MetricCard.types";
import SubSectionHeading from "@/components/SubSectionHeading";
import type { Path } from "@/router";
import type { BackgroundCheck } from "@/types";
import { BackgroundCheckDispositionModal } from "@/features/person/components/BackgroundCheckDisposition";
import { useCanManageBackgroundCheck } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import { canEditBackgroundCheckRequirementOnPtm } from "./backgroundCheckEligibility";
import {
  getRequirementExpirationDate,
  getRequirementScreenedDate,
  getRequirementStatusLabel,
  REQUIREMENT_MANAGEMENT_TOOLTIP
} from "./requirementSummary";
import { QUALIFYING_LEVEL_REQUIREMENT_HEADING } from "./backgroundCheckCopy";

interface BackgroundCheckRequirementPanelProps {
  pgaId: string;
  backgroundCheck?: BackgroundCheck | null;
  /** When false, the parent supplies a SectionHeading (focused page Section A). */
  showHeading?: boolean;
  /** When true, disposition edits use external actions instead of the linked metric card. */
  disableMetricEdit?: boolean;
  editOpen?: boolean;
  onEditOpenChange?: (open: boolean) => void;
}

function getRequirementStatusVariant(statusLabel: string): MetricCardVariant {
  switch (statusLabel) {
    case "Approved":
    case "Complete":
      return "success";
    case "Denied":
    case "Expired":
      return "error";
    case "In Progress":
    case "In Review":
    case "Documentation Needed":
      return "warn";
    default:
      return undefined;
  }
}

export default function BackgroundCheckRequirementPanel({
  pgaId,
  backgroundCheck,
  showHeading = true,
  disableMetricEdit = false,
  editOpen: controlledEditOpen,
  onEditOpenChange
}: BackgroundCheckRequirementPanelProps) {
  const flags = useFlags();
  const canManageBackgroundCheck = useCanManageBackgroundCheck();
  const canEditRequirement = canEditBackgroundCheckRequirementOnPtm({ flags, canManageBackgroundCheck });
  const [internalEditOpen, setInternalEditOpen] = useState(false);
  const editOpen = controlledEditOpen ?? internalEditOpen;
  const setEditOpen = onEditOpenChange ?? setInternalEditOpen;
  const metricEditEnabled = canEditRequirement && !disableMetricEdit;

  const requirementStatus = getRequirementStatusLabel(backgroundCheck);
  const screenedDate = getRequirementScreenedDate(backgroundCheck);
  const expirationDate = getRequirementExpirationDate(backgroundCheck);

  const metrics = useMemo((): MetricCardType<Path>[] => {
    const requirementStatusMetric: MetricCardType<Path> = {
      iconName: "assignment_turned_in",
      label: "Requirement Status",
      value: requirementStatus,
      variant: getRequirementStatusVariant(requirementStatus),
      tooltip: REQUIREMENT_MANAGEMENT_TOOLTIP,
      testId: "background-check-requirement-status-metric",
      ...(metricEditEnabled
        ? {
            onClick: () => setEditOpen(true),
            ariaLabel: "Edit qualifying level background check"
          }
        : {})
    };

    return [
      requirementStatusMetric,
      {
        iconName: "calendar_today",
        label: "Screened Date",
        value: screenedDate,
        testId: "background-check-requirement-screened-date-metric"
      },
      {
        iconName: "event",
        label: "Expiration",
        value: expirationDate,
        testId: "background-check-requirement-expiration-metric"
      }
    ];
  }, [metricEditEnabled, expirationDate, requirementStatus, screenedDate, setEditOpen]);

  const metricsGrid = (
    <div className="w-full" data-testid="background-check-requirement-metrics">
      <MetricGrid metrics={metrics} layout="balanced" hasHeading />
    </div>
  );

  return (
    <div
      className={showHeading ? "flex w-full flex-col items-start gap-2" : "w-full"}
      data-testid="background-check-requirement-panel"
    >
      {showHeading ? (
        <>
          <SubSectionHeading>{QUALIFYING_LEVEL_REQUIREMENT_HEADING}</SubSectionHeading>
          {metricsGrid}
        </>
      ) : (
        metricsGrid
      )}

      {canEditRequirement && (
        <BackgroundCheckDispositionModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          pgaId={pgaId}
          backgroundCheck={backgroundCheck}
        />
      )}
    </div>
  );
}
